import { useState } from "react";
import type { ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { FormContent } from "./components/FormContent";
import { FormValues, } from "./types";
import Copy from '../../../assets/copy.svg'
import { CopyPopup } from "./components/CopyPopup";
import Danger from '../../../assets/danger.svg'
import { ProgressSpinner } from "primereact/progressspinner";



interface AudioFile {
  file: File;
  url: string;
  id: string;
}

interface TrackInfoProps {
  audioFiles: AudioFile[];
  startTime: string;
  track?: Partial<FormValues>;
  formState: FormState
  editId: string
  release?: FormValues | null
  id: string | number
  fetching?: boolean
  onSubmit: any
  TurnOff: boolean
  hasValidationErrors: any
  values: any
  onSaveToList?: () => Promise<void>;
  onSaveAndNext?: () => Promise<void>;
  onSaveCurrent?: () => Promise<void>;
  isDirty?: boolean;
  caraoke: boolean;
  setCaraoke: (caraoke: boolean) => void;
  tracks:any
  savedTracksCount?: number;     // count of already-saved tracks (right-side list)
  isAddingNewTrack?: boolean;    // true when current form is for a "new" unsaved track
  hasAudioFile?: boolean;        // audio selected for current track
  onCopyFromPrevious?: () => void;
  bottomContent?: ReactNode;
  forceSaveTrack?: boolean;
  saving?: boolean;
}
interface FormState {
  ai_generated: boolean;
  explicit_content: boolean;
  instrumental_music: boolean;

}


export const TrackInfo = ({ audioFiles, track, formState, startTime, editId: trackId, release, onSubmit, TurnOff, hasValidationErrors, values, caraoke, setCaraoke, tracks, bottomContent, savedTracksCount = 0, isAddingNewTrack = false, hasAudioFile = false, onCopyFromPrevious, onSaveToList, onSaveAndNext, onSaveCurrent, isDirty = false, forceSaveTrack = false, saving = false}: TrackInfoProps) => {
  const { id } = useParams();
  const [showCopyPopup, setShowCopyPopup] = useState(false)

  const [showHint, setShowHint] = useState(false); 
  const navigate = useNavigate();
  // function generateISRC() {
  //   const country = "US"; 
  //   const registrant = "ABC";
  //   const year = String(new Date().getFullYear()).slice(-2); 

  //   const designation = String(Math.floor(Math.random() * 99999) + 1).padStart(5, "0");

  //   return country + registrant + year + designation;
  // }





  const handleCopyClick = () => {
    setShowCopyPopup(prev => !prev);
  };
  const handleApplyCopiedData = (copiedValues: Partial<FormValues>) => {
    // setTracks(prev => {
    //   if (!prev) return prev;
    //   return {
    //     ...prev,
    //     ...copiedValues,
    //   };
    // });
    // setShowCopyPopup(false);
  };

  // Primary CTA logic:
  // - Draft/new track => Add track to list
  // - Existing track w/ changes => Save track
  // - Otherwise => Select platforms and countries
  const showAddToList = !!isAddingNewTrack;
  const showSaveTrack = !showAddToList && (!!isDirty || !!forceSaveTrack);

  const label = TurnOff
    ? "Saving..."
    : showAddToList
      ? "Add track to list"
      : showSaveTrack
        ? "Save track"
        : "Select platforms and countries";

  // Require exactly one primary (MAIN) artist to be selected for each track
  const hasPrimaryArtist = Array.isArray(values?.artists) &&
    values.artists.some((a: any) => {
      const role = String(a?.role ?? a?.pivot?.role ?? "").toUpperCase();
      return (
        role === "MAIN" ||
        a?.is_primary === true ||
        a?.is_primary === 1 ||
        a?.is_primary === "1" ||
        a?.checked === true
      );
    });

  const disabled =
    !!hasValidationErrors ||
    !!TurnOff ||
    !hasPrimaryArtist ||
    (showAddToList && !hasAudioFile) ||
    (showSaveTrack && !!forceSaveTrack && !hasAudioFile);

  const missingFields: string[] = [];
  if (showAddToList && !hasAudioFile) missingFields.push("Track upload (audio file)");
  if (!values?.artists?.length) missingFields.push("Artists");
  if (!hasPrimaryArtist) missingFields.push("Primary artist (select one main artist)");
  if (!values?.title || String(values.title).trim().length === 0) missingFields.push("Track title");
  // Track version is optional
  if (!values?.metadata_language || String(values.metadata_language).trim().length === 0) missingFields.push("Track language");
  const ry = Number(values?.recording_year as any);
  if (!Number.isFinite(ry) || ry <= 0) missingFields.push("Recording year");
  if (!values?.contributors?.length) missingFields.push("Contributors");
  const mainGenre = Array.isArray(values?.main_genre) ? values.main_genre?.[0] : values?.main_genre;
  if (mainGenre == null || String(mainGenre).trim() === "") missingFields.push("Primary genre");
  if (!caraoke) {
    if (!values?.lyrics || String(values.lyrics).trim().length === 0) missingFields.push("Lyrics");
  }

  const onClick = async () => {
    if (process.env.NODE_ENV !== "production") {
      console.log("[TrackInfo] Primary action clicked", {
        releaseId: id,
        mode: showAddToList ? "add_to_list" : showSaveTrack ? "save_track" : "save_and_next",
      });
    }
    try {
      if (showAddToList) {
        await onSaveToList?.();
        return;
      }
      if (showSaveTrack) {
        await onSaveCurrent?.();
        return;
      }
      await onSaveAndNext?.();
    } catch (e) {
      console.error("[TrackInfo] Primary action failed", e);
    }
  };

  return (

    <Container>
      {saving && (
        <BlockLoadingOverlay>
          <ProgressSpinner style={{ width: "48px", height: "48px" }} />
        </BlockLoadingOverlay>
      )}
      <HeaderBox>
        <Header
          title={
            <FormattedMessage
              id="NewRelease.TrackInfo.Title"
              defaultMessage="Track information (Step 5/6)"
            />
          }
        />
        {typeof onCopyFromPrevious === "function" && !!isAddingNewTrack && Number(savedTracksCount) > 0 && (
          <HeaderActionButton
            type="button"
            onClick={() => onCopyFromPrevious()}
          >
            Copy from previous
          </HeaderActionButton>
        )}
        {/* <Box onClick={handleCopyClick}>
          <span>
            <FormattedMessage
              id="NewRelease.TrackInfo.CopyData"
              defaultMessage="Copy data"
            />
          </span>
          <Copy />
        </Box> */}
      </HeaderBox>

      {showCopyPopup && (
        <CopyPopup
          audioFiles={audioFiles}
          currentValues={values}
          onApply={handleApplyCopiedData}
        />
      )}
      <FormContent values={values} release={release} caraoke={caraoke} setCaraoke={setCaraoke} trackId={trackId} tracks={tracks}/>

      {bottomContent}

      <FooterWrapper
        onMouseEnter={() => setShowHint(true)}
        onMouseLeave={() => setShowHint(false)}
      >
        <Footer
          nextButtonText={label}
          onNext={onClick}
          disabled={disabled}
        />
        {showHint && disabled && missingFields.length > 0 && (
          <HoverPopup>
            <HoverPopupTitleBox>
              <Danger /> Fill in the fields to continue
            </HoverPopupTitleBox>
            <TextBox>
              {missingFields.map((f: string) => (
                <Text key={f}>{f};</Text>
              ))}
            </TextBox>
          </HoverPopup>
        )}
        
      </FooterWrapper>
    </Container>



  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 38px 20px 30px;
  height: 100%;
  position:relative;
`;

const BlockLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  border-radius: 14px;
`;

const HeaderBox = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: SF Pro Display;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: #A4A4A4;
  align-items: center;
  
`;

const HeaderActionButton = styled.button`
  height: 32px;
  padding: 0 10px;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
`;

const Box = styled.div`
  display: flex;
  gap: 5px;
  cursor: pointer;
`;

const FooterWrapper = styled.div`
  width:100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  gap: 10px;
`;

const HoverPopup = styled.div`
  position: absolute;
  bottom: 120%;
  right: 0;
  width: 236px;
  height: auto;
  opacity: 1;
  border-radius: 15px;
  background: rgba(226, 229, 255, 1);
  box-shadow: 0px 0px 10px 0px rgba(145, 145, 145, 0.47);
  padding: 17px 21px 14px 14px;
  display: flex;
  flex-direction: column;
  gap:9px;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HoverPopupTitleBox = styled.div`
  display: flex;
  gap:6px;
  font-family: SF Pro Display;
  font-weight: 600;
  font-style: Semibold;
  font-size: 15px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: 0%;
  color:rgba(0, 0, 0, 1);
`

const Text = styled.div`
  font-family: SF Pro Display;
  font-weight: 400;
  font-style: Regular;
  font-size: 15px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: 0%;
  color:rgba(0, 0, 0, 1);
`
const TextBox = styled.div`
`
const Loading = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;